import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../../components/layout/MainLayout';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { getTenderDashboardStats, getTenders } from '../../services/tenderService';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import toast from 'react-hot-toast';

const COLORS = ['#3b82f6', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4'];

const StatCard = ({ label, value, icon, color, sub = undefined }) => (
  <div className="bg-card border border-border rounded-2xl p-5">
    <div className="flex items-start justify-between mb-3">
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${color}`}>
        <Icon name={icon} size={20} className="text-white" />
      </div>
    </div>
    <div className="text-3xl font-bold text-foreground">{value}</div>
    <div className="text-sm text-muted-foreground mt-0.5">{label}</div>
    {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
  </div>
);

const TenderDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [tenders, setTenders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [s, t] = await Promise.all([getTenderDashboardStats(), getTenders()]);
      setStats(s);
      setTenders(t);
    } catch {
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-64">
          <Icon name="Loader2" size={32} className="animate-spin text-primary" />
        </div>
      </MainLayout>
    );
  }

  // Chart data
  const statusData = stats?.tenders
    ? Object.entries(
        stats.tenders.reduce((acc, t) => { acc[t.status] = (acc[t.status] || 0) + 1; return acc; }, {})
      ).map(([name, value]) => ({ name: name.charAt(0).toUpperCase() + name.slice(1), value }))
    : [];

  const categoryData = stats?.tenders
    ? Object.entries(
        stats.tenders.reduce((acc, t) => { acc[t.tender_category || 'Other'] = (acc[t.tender_category || 'Other'] || 0) + 1; return acc; }, {})
      ).map(([name, value]) => ({ name, value }))
    : [];

  const complianceData = tenders
    .filter((t) => t.complianceOverall > 0)
    .slice(0, 8)
    .map((t) => ({
      name: t.tenderName?.substring(0, 12) + (t.tenderName?.length > 12 ? '...' : ''),
      compliance: t.complianceOverall,
    }));

  const taskStatusData = stats?.tasks
    ? Object.entries(
        stats.tasks.reduce((acc, t) => { acc[t.task_status] = (acc[t.task_status] || 0) + 1; return acc; }, {})
      ).map(([name, value]) => ({ name: name.replace('_', ' '), value }))
    : [];

  const recentTenders = tenders.slice(0, 5);

  return (
    <MainLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
              <Icon name="LayoutDashboard" size={24} className="text-primary" />
              Tender Dashboard
            </h1>
            <p className="text-muted-foreground text-sm mt-0.5">Overview of all tender activities and compliance</p>
          </div>
          <Button onClick={() => navigate('/tender-analyzer')} className="gap-2">
            <Icon name="Plus" size={16} />
            New Tender
          </Button>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard label="Total Tenders" value={stats?.total || 0} icon="FileText" color="bg-primary" />
          <StatCard label="Open Tenders" value={stats?.open || 0} icon="FolderOpen" color="bg-blue-500" />
          <StatCard label="Analyzed" value={stats?.analyzed || 0} icon="Brain" color="bg-purple-500" />
          <StatCard label="Avg Compliance" value={`${stats?.avgCompliance || 0}%`} icon="ShieldCheck" color="bg-success" />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard label="High Risk" value={stats?.highRisk || 0} icon="AlertTriangle" color="bg-error" sub="Compliance < 70%" />
          <StatCard label="Upcoming Deadlines" value={stats?.upcoming || 0} icon="Clock" color="bg-warning" sub="Within 7 days" />
          <StatCard label="Pending Tasks" value={stats?.pendingTasks || 0} icon="ListTodo" color="bg-orange-500" />
          <StatCard label="Closed" value={stats?.closed || 0} icon="CheckCircle2" color="bg-teal-500" />
        </div>

        {/* Charts Row 1 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {/* Compliance Chart */}
          <div className="bg-card border border-border rounded-2xl p-5">
            <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
              <Icon name="BarChart2" size={18} className="text-primary" />
              Compliance by Tender
            </h3>
            {complianceData.length > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={complianceData} margin={{ top: 5, right: 10, left: -20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                  <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#6b7280' }} />
                  <YAxis domain={[0, 100]} tick={{ fontSize: 10, fill: '#6b7280' }} />
                  <Tooltip
                    contentStyle={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '12px' }}
                    formatter={(v) => [`${v}%`, 'Compliance']}
                  />
                  <Bar dataKey="compliance" radius={[4, 4, 0, 0]}>
                    {complianceData.map((entry, i) => (
                      <Cell key={i} fill={entry.compliance >= 90 ? '#22c55e' : entry.compliance >= 75 ? '#f59e0b' : '#ef4444'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-48 text-muted-foreground text-sm">No analyzed tenders yet</div>
            )}
          </div>

          {/* Status Pie */}
          <div className="bg-card border border-border rounded-2xl p-5">
            <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
              <Icon name="PieChart" size={18} className="text-primary" />
              Tender Status Distribution
            </h3>
            {statusData.length > 0 ? (
              <div className="flex items-center gap-4">
                <ResponsiveContainer width="60%" height={200}>
                  <PieChart>
                    <Pie data={statusData} cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={3} dataKey="value">
                      {statusData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Tooltip contentStyle={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '12px' }} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex-1 space-y-2">
                  {statusData.map((item, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm">
                      <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ background: COLORS[i % COLORS.length] }} />
                      <span className="text-muted-foreground flex-1">{item.name}</span>
                      <span className="font-semibold text-foreground">{item.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center h-48 text-muted-foreground text-sm">No tenders yet</div>
            )}
          </div>
        </div>

        {/* Charts Row 2 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {/* Category Distribution */}
          <div className="bg-card border border-border rounded-2xl p-5">
            <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
              <Icon name="Tag" size={18} className="text-primary" />
              Tenders by Category
            </h3>
            {categoryData.length > 0 ? (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={categoryData} layout="vertical" margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 10, fill: '#6b7280' }} />
                  <YAxis dataKey="name" type="category" tick={{ fontSize: 11, fill: '#6b7280' }} width={80} />
                  <Tooltip contentStyle={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '12px' }} />
                  <Bar dataKey="value" fill="#3b82f6" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-48 text-muted-foreground text-sm">No data</div>
            )}
          </div>

          {/* Task Status */}
          <div className="bg-card border border-border rounded-2xl p-5">
            <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
              <Icon name="ListTodo" size={18} className="text-primary" />
              Task Status Overview
            </h3>
            {taskStatusData.length > 0 ? (
              <div className="flex items-center gap-4">
                <ResponsiveContainer width="60%" height={200}>
                  <PieChart>
                    <Pie data={taskStatusData} cx="50%" cy="50%" outerRadius={80} paddingAngle={3} dataKey="value">
                      {taskStatusData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Tooltip contentStyle={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '12px' }} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex-1 space-y-2">
                  {taskStatusData.map((item, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm">
                      <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ background: COLORS[i % COLORS.length] }} />
                      <span className="text-muted-foreground flex-1 capitalize">{item.name}</span>
                      <span className="font-semibold text-foreground">{item.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center h-48 text-muted-foreground text-sm">No tasks yet</div>
            )}
          </div>
        </div>

        {/* Recent Tenders */}
        <div className="bg-card border border-border rounded-2xl p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-foreground flex items-center gap-2">
              <Icon name="Clock" size={18} className="text-primary" />
              Recent Uploads
            </h3>
            <button onClick={() => navigate('/tender-uploaded')} className="text-xs text-primary hover:underline">View all</button>
          </div>
          {recentTenders.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground text-sm">No tenders uploaded yet</div>
          ) : (
            <div className="space-y-2">
              {recentTenders.map((t) => {
                const compliance = t.complianceOverall || 0;
                return (
                  <div key={t.id} className="flex items-center justify-between bg-muted/20 rounded-xl px-4 py-3 hover:bg-muted/40 transition-colors cursor-pointer"
                    onClick={() => t.status === 'analyzed' && navigate(`/tender-compliance/${t.id}`)}>
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Icon name="FileText" size={16} className="text-primary" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground">{t.tenderName}</p>
                        <p className="text-xs text-muted-foreground">{t.clientName || '—'} • {t.tenderCategory}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {compliance > 0 && (
                        <div className="flex items-center gap-1.5">
                          <div className="w-12 h-1.5 bg-muted rounded-full overflow-hidden">
                            <div className={`h-full rounded-full ${compliance >= 90 ? 'bg-success' : compliance >= 75 ? 'bg-warning' : 'bg-error'}`}
                              style={{ width: `${compliance}%` }} />
                          </div>
                          <span className={`text-xs font-semibold ${compliance >= 90 ? 'text-success' : compliance >= 75 ? 'text-warning' : 'text-error'}`}>
                            {compliance}%
                          </span>
                        </div>
                      )}
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                        t.status === 'analyzed' ? 'bg-success/15 text-success' :
                        t.status === 'draft' ? 'bg-muted text-muted-foreground' : 'bg-blue-500/15 text-blue-400'
                      }`}>{t.status}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </MainLayout>
  );
};

export default TenderDashboard;
